import { useTranslation } from "react-i18next";
import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import { Input } from "@/components/ui/input";
import { QuestionnaireFormSection } from "../shared/QuestionnaireFormSection";
import ProvinceSelect from "@/components/ProvinceSelect";
import TaxOfficeSelect from "@/components/TaxOfficeSelect";
import CitizenshipSelect from "@/components/CitizenshipSelect";
import DisabilityCertificateSelect from "@/components/DisabilityCertificateSelect";
import GenderSelect from "@/components/GenderSelect";
import { useQuestionnaireContext } from "../../context/QuestionnaireContext";

interface QuestionnaireDataSectionProps {
  disabled?: boolean;
}

export function QuestionnaireDataSection({
  disabled = false,
}: QuestionnaireDataSectionProps) {
  const { t } = useTranslation();
  const { disabilityCertificateQuery, questionnaireQuery } =
    useQuestionnaireContext();

  // TODO: This will be fixed in another task
  const questionnaireData = questionnaireQuery?.data?.questionnaire as
    | {
        citizenship_name?: string;
        disability_certificate_code?: string;
        province_name?: string;
        province_nfz_code?: string;
        province_nfz_description?: string;
        tax_office_name?: string;
        is_b2b_agreement?: boolean;
      }
    | undefined;

  return (
    <QuestionnaireFormSection
      title={t("questionnaires.form.dataSection.title")}
    >
      <Field
        name="first_name"
        label={t("questionnaires.form.fields.first_name")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="last_name"
        label={t("questionnaires.form.fields.last_name")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="city"
        label={t("questionnaires.form.fields.city")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="postal_code"
        label={t("questionnaires.form.fields.postal_code")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="post_office"
        label={t("questionnaires.form.fields.post_office")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="street"
        label={t("questionnaires.form.fields.street")}
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="house_number"
        label={t("questionnaires.form.fields.house_number")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="apartment_number"
        label={t("questionnaires.form.fields.apartment_number")}
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="province_id"
        label={t("questionnaires.form.fields.province")}
        required
        render={({ field }) => (
          <ProvinceSelect
            {...field}
            loading="eager"
            value={field.value?.toString()}
            defaultItem={{
              id: field.value?.toString() ?? "",
              name: questionnaireData?.province_name,
              nfz_code: questionnaireData?.province_nfz_code,
              nfz_description: questionnaireData?.province_nfz_description,
            }}
            showNfzFooter
            disabled={disabled}
          />
        )}
      />

      <Field
        name="county"
        label={t("questionnaires.form.fields.county")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="municipality"
        label={t("questionnaires.form.fields.municipality")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="email"
        label={t("questionnaires.form.fields.email")}
        required
        render={({ field }) => <Input type="email" {...field} disabled />}
      />

      <Field
        name="phone"
        label={t("questionnaires.form.fields.phone")}
        required
        render={({ field }) => (
          <Input
            {...field}
            placeholder={t("questionnaires.form.fields.phone_placeholder")}
            disabled={disabled}
          />
        )}
      />

      <Field
        name="birth_date"
        label={t("questionnaires.form.fields.birth_date")}
        required
        render={({ field }) => (
          <DatePicker className="w-full" {...field} disabled />
        )}
      />

      <Field
        name="personal_id_number"
        label={t("questionnaires.form.fields.personal_id_number")}
        required
        render={({ field }) => <Input {...field} disabled />}
      />

      <Field
        name="birth_place"
        label={t("questionnaires.form.fields.birth_place")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="gender"
        label={t("questionnaires.form.fields.gender")}
        required
        render={({ field }) => <GenderSelect {...field} disabled={disabled} />}
        className="hidden"
      />

      <Field
        name="bank_account_number"
        label={t("questionnaires.form.fields.bank_account_number")}
        required
        render={({ field }) => <Input {...field} disabled={disabled} />}
      />

      <Field
        name="tax_office_id"
        label={t("questionnaires.form.fields.tax_office")}
        required
        render={({ field }) => (
          <TaxOfficeSelect
            {...field}
            loading="eager"
            value={field.value?.toString()}
            defaultItem={{
              id: field.value?.toString() ?? "",
              name: questionnaireData?.tax_office_name,
            }}
            disabled={disabled}
          />
        )}
      />

      <Field
        name="citizenship_id"
        label={t("questionnaires.form.fields.citizenship")}
        required
        render={({ field }) => (
          <CitizenshipSelect
            {...field}
            loading="eager"
            value={field.value?.toString()}
            defaultItem={{
              id: field.value?.toString() ?? "",
              name: questionnaireData?.citizenship_name,
            }}
            disabled={disabled}
          />
        )}
      />

      <div className="space-y-2">
        <Field
          name="disability_certificate_id"
          label={t("questionnaires.form.fields.disability_certificate")}
          required
          render={({ field }) => (
            <DisabilityCertificateSelect
              {...field}
              loading="eager"
              value={field.value?.toString()}
              defaultItem={{
                id: field.value?.toString() ?? "",
                name: questionnaireData?.disability_certificate_code,
              }}
              disabled={disabled}
            />
          )}
        />

        {disabilityCertificateQuery.data?.items && (
          <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
            <div className="space-y-0.5">
              {disabilityCertificateQuery.data.items.map((item) => (
                <div key={item.id} className="text-gray-600">
                  <span className="font-medium">{item.code}:</span>{" "}
                  {item.description}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {questionnaireData?.is_b2b_agreement && (
        <>
          <Field
            name="company_tax_identification_number"
            label={t(
              "questionnaires.form.fields.company_tax_identification_number",
            )}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_name"
            label={t("questionnaires.form.fields.company_name")}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_postal_code"
            label={t("questionnaires.form.fields.company_postal_code")}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_city"
            label={t("questionnaires.form.fields.company_city")}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_street_name"
            label={t("questionnaires.form.fields.company_street_name")}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_street_number"
            label={t("questionnaires.form.fields.company_street_number")}
            required
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />

          <Field
            name="company_apartment_number"
            label={t("questionnaires.form.fields.company_apartment_number")}
            render={({ field }) => <Input {...field} disabled={disabled} />}
          />
        </>
      )}
    </QuestionnaireFormSection>
  );
}
